import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { catchError, of } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";

import { JobCardComponent } from "src/app/shared/components/network-card/job-card.component";
import {
  SearchContainerComponent,
  SearchDataSource,
  SearchParams,
  SearchResult,
  SearchState,
  buildApiParams,
} from "src/app/shared/components/search";

import { JobPosting } from "src/app/api/models/job-posting";
import { JobPostingPagedData } from "src/app/api/models/job-posting-paged-data";
import { JobPostingService } from "src/app/api/services/job-posting.service";
import { GetJobPostings$Params } from "src/app/api/fn/job-posting/get-job-postings";

import { DomainService } from "src/app/shared/services/domain.service";
import { FilterOptionsService } from "src/app/shared/services/filter-options.service";

@Component({
  selector: "app-view-public-vacancies",
  providers: [SearchState],
  imports: [TranslateModule, SearchContainerComponent, JobCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-search-container
      [config]="searchConfig()"
      [dataSource]="dataSource"
      [fields]="fields()"
      [trackByFn]="trackByJobId"
    >
      <ng-template
        #itemTemplate
        let-job
        let-isHighlighted="isHighlighted"
        let-layout="layout"
      >
        <app-job-card
          [data]="job"
          [isHighlighted]="isHighlighted"
          [layout]="layout ?? 'portrait'"
        ></app-job-card>
      </ng-template>
    </app-search-container>
  `,
})
export class ViewPublicVacanciesComponent {
  private readonly domainService = inject(DomainService);
  private readonly jobPostingService = inject(JobPostingService);
  private readonly searchState = inject(SearchState);
  private readonly filterOptionsService = inject(FilterOptionsService);

  fields = computed<FormlyFieldConfig[]>(() => [
    this.filterOptionsService.searchTermField(),
    this.filterOptionsService.buildLocationField({ label: "Location" }),
    this.filterOptionsService.jobPostingTypesField(),
    this.filterOptionsService.areaOfInterestsField(),
    this.filterOptionsService.educationLevelsField(),
    this.filterOptionsService.employeeRangeField(),
  ]);

  searchConfig = computed(() => ({
    searchOptions: this.searchState.searchOptions(),
    context: {
      queryParamOptions: [
        "Location",
        "MaximumDistance",
        "AreaOfInterests",
        "EducationLevels",
        "SearchTerm",
        "NumberOfEmployeesLower",
        "NumberOfEmployeesUpper",
        "JobPostingType",
      ],
    },
    filterDisplayNames: {
      SearchTerm: "Search",
      MaximumDistance: "Radius",
      AreaOfInterests: "Field of interest",
      EducationLevels: "Education level",
      JobPostingType: "Job types",
      NumberOfEmployeesLower: "Employees min.",
      NumberOfEmployeesUpper: "Employees max.",
    },
  }));

  dataSource: SearchDataSource<JobPosting> = {
    fetch: async (params: SearchParams): Promise<SearchResult<JobPosting>> => {
      const apiParams = buildApiParams<GetJobPostings$Params>(
        params,
        this.domainService.getCurrentRegion()
      );

      const result = await firstValueFrom(
        this.jobPostingService
          .getJobPostings(apiParams)
          .pipe(
            catchError(() =>
              of({ data: [], totalItems: 0 } as JobPostingPagedData)
            )
          )
      );

      return {
        data: result?.data || [],
        totalItems: result?.totalItems || 0,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      };
    },
  };

  readonly trackByJobId = (_index: number, job: JobPosting): string =>
    job.id || "";
}
